import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { AUTH_GRADIENT_STYLE } from "../../constants/authVisuals";

const COPY = {
  en: {
    title: "Hosting rules",
    rules: [
      {
        title: "No participants outside the list",
        body: "Everyone who comes to your event must be on that event's participant list on VaultGrid.",
      },
      {
        title: "Confirm attendance",
        body: "When participants arrive, check that everyone has confirmed attendance. They can use geolocation, or you can scan the QR code on their phone and confirm them manually.",
      },
      {
        title: "No promotion of products or services",
        body: "Events created to promote products or services are prohibited, including indirect or hidden advertising.",
      },
    ],
    consequence: "Violating the community or hosting rules leads to restricted access to the platform.",
    consent: "Yes, I understand all the rules and confirm that my event will follow them.",
    continue: "Continue",
    done: "Done",
    back: "Back",
    retry: "Could not save your confirmation. Please try again.",
  },
  ru: {
    title: "Правила организации",
    rules: [
      {
        title: "Участники вне списка запрещены",
        body: "Каждый, кто придёт на твой ивент, должен быть в списке участников этого ивента на VaultGrid.",
      },
      {
        title: "Подтверди участие",
        body: "Когда участники придут, проверь, что все подтвердили участие. Они могут сделать это через геопозицию, либо ты можешь отсканировать QR-код на их телефоне и подтвердить участие вручную.",
      },
      {
        title: "Без продвижения продуктов и услуг",
        body: "Ивенты, созданные для продвижения продуктов или услуг, запрещены. Это касается и скрытой или косвенной рекламы.",
      },
    ],
    consequence: "Нарушение правил сообщества и организации ивентов ведёт к ограничению доступа к платформе.",
    consent: "Да, я понимаю все правила и подтверждаю, что мой ивент будет проведён в соответствии с ними.",
    continue: "Продолжить",
    done: "Готово",
    back: "Назад",
    retry: "Не удалось сохранить подтверждение. Попробуйте ещё раз.",
  },
  sk: {
    title: "Pravidlá organizovania",
    rules: [
      {
        title: "Účastníci mimo zoznamu nie sú povolení",
        body: "Každý, kto príde na tvoj event, musí byť v zozname účastníkov tohto eventu na VaultGrid.",
      },
      {
        title: "Potvrď účasť",
        body: "Keď účastníci prídu, skontroluj, či majú všetci potvrdenú účasť. Môžu ju potvrdiť cez geolokáciu, alebo naskenuj QR kód na ich telefóne a potvrď ju manuálne.",
      },
      {
        title: "Bez propagácie produktov a služieb",
        body: "Eventy vytvorené na propagáciu produktov alebo služieb sú zakázané. Platí to aj pre skrytú či nepriamu reklamu.",
      },
    ],
    consequence: "Porušenie pravidiel komunity a organizovania eventov vedie k obmedzeniu prístupu na platformu.",
    consent: "Áno, rozumiem všetkým pravidlám a potvrdzujem, že môj event bude v súlade s nimi.",
    continue: "Pokračovať",
    done: "Hotovo",
    back: "Späť",
    retry: "Potvrdenie sa nepodarilo uložiť. Skús to znova.",
  },
  cs: {
    title: "Pravidla pořádání",
    rules: [
      {
        title: "Účastníci mimo seznam nejsou povoleni",
        body: "Každý, kdo přijde na tvůj event, musí být v seznamu účastníků tohoto eventu na VaultGrid.",
      },
      {
        title: "Potvrď účast",
        body: "Když účastníci přijdou, zkontroluj, zda mají všichni potvrzenou účast. Mohou ji potvrdit přes polohu, nebo naskenuj QR kód na jejich telefonu a potvrď ji ručně.",
      },
      {
        title: "Bez propagace produktů a služeb",
        body: "Eventy vytvořené za účelem propagace produktů nebo služeb jsou zakázané. Platí to i pro skrytou či nepřímou reklamu.",
      },
    ],
    consequence: "Porušení pravidel komunity a pořádání eventů vede k omezení přístupu k platformě.",
    consent: "Ano, rozumím všem pravidlům a potvrzuji, že můj event bude v souladu s nimi.",
    continue: "Pokračovat",
    done: "Hotovo",
    back: "Zpět",
    retry: "Potvrzení se nepodařilo uložit. Zkus to znovu.",
  },
};

export default function HostEventRulesIntro({ onBack, onContinue, reviewOnly = false }) {
  const { i18n } = useTranslation("events");
  const language = String(i18n.resolvedLanguage || i18n.language || "en").toLowerCase();
  const copy = COPY[language.slice(0, 2)] || COPY.en;
  const [understood, setUnderstood] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(false);

  const handleContinue = async () => {
    if ((!understood && !reviewOnly) || submitting) return;
    setSubmitting(true);
    setSubmitError(false);
    try {
      await onContinue();
    } catch {
      setSubmitError(true);
      setSubmitting(false);
    }
  };

  return (
    <div className="relative h-full min-h-0 w-full overflow-hidden text-white" style={AUTH_GRADIENT_STYLE}>
      <div className="relative mx-auto flex h-full min-h-0 w-full max-w-[402px] flex-col px-[30px] lg:max-w-[520px] lg:px-[26px]">
        <div className="absolute left-[25px] top-[calc(20px+var(--vg-safe-area-top))] h-9 w-9 rounded-full bg-white/10" />
        <button type="button" onClick={onBack} aria-label={copy.back} className="absolute left-[30px] top-[calc(26px+var(--vg-safe-area-top))] z-10 flex h-6 w-6 items-center justify-center text-white">
          <svg aria-hidden="true" viewBox="0 0 24 24" className="h-6 w-6" fill="none"><path d="M15 6L9 12L15 18" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </button>
        <div className="absolute left-1/2 top-[calc(36px+var(--vg-safe-area-top))] h-[5px] w-[230px] -translate-x-1/2 overflow-hidden rounded-[27px] bg-white/20" data-event-create-progress="track">
          <div className="h-full w-[8%] rounded-[27px] bg-white" data-event-create-progress="fill" />
        </div>

        <div className="-mx-6 min-h-0 flex-1 overflow-y-auto overscroll-contain px-6 pb-[18px] [-webkit-overflow-scrolling:touch] custom-scrollbar">
          <h1 className="mx-auto mt-[calc(82px+var(--vg-safe-area-top))] max-w-[310px] text-center text-[32px] font-semibold leading-[1.1] tracking-[0] text-white max-[850px]:mt-[calc(66px+var(--vg-safe-area-top))] max-[850px]:text-[30px]">{copy.title}</h1>
          <div className="mt-[30px] space-y-[12px] max-[850px]:mt-[20px]">
            {copy.rules.map((rule, index) => (
              <section key={rule.title} className="rounded-[18px] border border-white/25 bg-black/10 px-[18px] py-[16px]">
                <div className="flex items-start gap-[12px]">
                  <span aria-hidden="true" className="w-[24px] shrink-0 text-[26px] font-bold leading-[1] text-white">{index + 1}</span>
                  <div>
                    <h2 className="text-[18px] font-semibold leading-[1.2]">{rule.title}</h2>
                    <p className="mt-[8px] text-[14px] leading-[1.45] text-white/75">{rule.body}</p>
                  </div>
                </div>
              </section>
            ))}
            <p className="px-[2px] text-[12px] leading-[1.45] text-white/60">{copy.consequence}</p>
            {!reviewOnly ? <label className="flex cursor-pointer items-start gap-[12px] rounded-[18px] border border-white/25 bg-black/10 px-[16px] py-[14px] text-[13px] leading-[1.4] text-white">
              <input type="checkbox" checked={understood} onChange={(event) => setUnderstood(event.target.checked)} className="mt-[2px] h-[18px] w-[18px] shrink-0 accent-[#3e6ffb]" />
              <span>{copy.consent}</span>
            </label> : null}
          </div>
        </div>

        <div className="-mx-[30px] mt-auto shrink-0 px-[30px] pb-[max(env(safe-area-inset-bottom,0px),14px)] pt-[24px]">
          {submitError ? <p role="alert" className="mb-3 text-center text-[13px] text-white">{copy.retry}</p> : null}
          <button type="button" disabled={(!understood && !reviewOnly) || submitting} onClick={handleContinue} className="h-[55px] w-full rounded-[100px] border border-white/20 bg-[#3e6ffb] text-[15px] font-semibold leading-4 text-white disabled:cursor-not-allowed disabled:opacity-45">{reviewOnly ? copy.done : copy.continue}</button>
        </div>
      </div>
    </div>
  );
}
